import * as React from "react";
import { useSelector, useDispatch } from "@teamprevent/admin-redux";

import { PhysiotherapyForm } from "@teamprevent/common/physiotherapy/components/PhysiotherapyForm";
import { PhysiotherapyMgrReferral } from "@teamprevent/common/physiotherapy/components/PhysiotherapyMgrReferral";
import {
    PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY,
    employeeDetailSectionId,
    furtherDetailsSectionId,
    lineManagerHrSectionId
} from "@teamprevent/common/physiotherapy/models";
import { updateQuestion } from "@teamprevent/common/questionnaires/actions";
import { CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { AnsweredQuestion } from "@teamprevent/common/model";
import * as questionIds from "@teamprevent/common/questionnaires/questionnaireConfig/physiotherapyMgrReferral/questionIds";

import { loadQuestionnaireConfig } from "../../../questionnaireConfig/actions";

import { ApplicationState } from "../../../applicationState";
import { selectAndMergePhysiotherapyMgrReferralQuestionnaireConfig } from "../../selectors";
import { routeIdSelector as customerIdSelector } from "../../../crud/customer/selectors";
import { routeIdSelector as employeeRecordIdSelector, employeeRecordSelector } from "../../../crud/employeeRecord/selectors";
import { ohCaseFromIdSelector } from "../../../crud/ohCase/selectors";
import { loadOrgStructures } from "../../../crud/orgStructure";
import { OhCase } from "../../../crud/ohCase/model";

import { push } from "redux-little-router";

const PhysiotherapyMgrReferralForm: React.FunctionComponent = () => {
    const dispatch = useDispatch();

    const customerId = useSelector((state: ApplicationState) => customerIdSelector(state));
    const employeeRecordId = useSelector((state: ApplicationState) => employeeRecordIdSelector(state));
    const employeeRecord = useSelector((state: ApplicationState) => employeeRecordSelector(state));
    const questionnaireConfig = useSelector((state: ApplicationState) => selectAndMergePhysiotherapyMgrReferralQuestionnaireConfig(state));
    const linkedOhCaseId = useSelector((state: ApplicationState) => state.router.params?.linkedOhCaseId);
    const linkedOhCaseRecord: OhCase = useSelector((state: ApplicationState) => ohCaseFromIdSelector(state, linkedOhCaseId));

    React.useEffect(() => {
        setAnswer(employeeDetailSectionId, questionIds.ReferredTitle, employeeRecord.title);
        setAnswer(employeeDetailSectionId, questionIds.ReferredFirstName, employeeRecord.firstName);
        setAnswer(employeeDetailSectionId, questionIds.ReferredLastName, employeeRecord.lastName);
        setAnswer(employeeDetailSectionId, questionIds.ReferredDob, employeeRecord.dateOfBirth?.format("YYYY-MM-DD"));
        setAnswer(employeeDetailSectionId, questionIds.ReferredGender, employeeRecord.gender);
        setAnswer(employeeDetailSectionId, questionIds.EmployeeId, employeeRecord.secondaryReference);
        setAnswer(employeeDetailSectionId, questionIds.JobTitle, employeeRecord.jobTitle);
        setAnswer(employeeDetailSectionId, questionIds.Postcode, employeeRecord.postCode);
        setAnswer(employeeDetailSectionId, questionIds.EmailAddress, employeeRecord.personalEmailAddress);
        setAnswer(employeeDetailSectionId, questionIds.ConfirmEmailAddress, employeeRecord.personalEmailAddress);
        setAnswer(employeeDetailSectionId, questionIds.MobilePhone, employeeRecord.mobilePhoneNumber);
        setAnswer(employeeDetailSectionId, questionIds.HomePhone, employeeRecord.homePhoneNumber);
        setAnswer(furtherDetailsSectionId, questionIds.OrgLevel1Id, employeeRecord.orgStructureOne?.id, questionIds.OrgLevel1Name);
        setAnswer(furtherDetailsSectionId, questionIds.OrgLevel2Id, employeeRecord.orgStructureTwo?.id, questionIds.OrgLevel2Name);
        setAnswer(furtherDetailsSectionId, questionIds.OrgLevel3Id, employeeRecord.orgStructureThree?.id, questionIds.OrgLevel3Name);
        setAnswer(furtherDetailsSectionId, questionIds.OrgLevel4Id, employeeRecord.orgStructureFour?.id, questionIds.OrgLevel4Name);
    }, [questionnaireConfig, employeeRecord]);

    React.useEffect(() => {
        if (linkedOhCaseRecord?.purchaseOrderNumber) {
            setAnswer(lineManagerHrSectionId, questionIds.PurchaseOrderNumber, linkedOhCaseRecord.purchaseOrderNumber);
        }
    }, [questionnaireConfig, linkedOhCaseRecord]);

    React.useEffect(() => {
        dispatch(loadQuestionnaireConfig({ customerId, questionnaireType: "physiotherapyMgrReferral" }));
    }, [customerId, dispatch]);

    const goToCase = (ohCaseId: string) => {
        dispatch(push(`/customers/${customerId}/employeeRecords/${employeeRecordId}/ohCases/${ohCaseId}`));
    };

    const loadOrgStructure = (parentOrgStructureId: string) => {
        dispatch(loadOrgStructures({ customerId, parentOrgStructureId }));
    };

    const updateAnswer = (id: string, question: AnsweredQuestion, questionnaireKey: string, sectionId: string) => {
        dispatch(updateQuestion({ id, question, questionnaireKey, sectionId }));
    };

    const setAnswer = (sectionId: string, questionId: string, answer: string, questionConfigId?: string) => {
        if (!questionnaireConfig) {
            return;
        }
        const questionConfig =
            questionnaireConfig.questions.find(q => q.id === (questionConfigId || questionId)) as CustomQuestionConfig;
        updateAnswer(
            questionId,
            { question: questionConfig.questionText, sectionId, id: questionId, answer, isValid: true },
            PHYSIOTHERAPY_MGR_REFERRAL_QUESTIONNAIRE_KEY,
            sectionId);
    };

    return (
        <PhysiotherapyForm
            readonly={false}
            questionnaireConfig={questionnaireConfig}
            afterSubmit={goToCase}
            physiotherapyReferralFormComponent={PhysiotherapyMgrReferral}
            api={`/api/customer/${customerId}/employeeRecord/${employeeRecordId}/physiotherapyMgrReferralCase`}
            loadOrgStructure={loadOrgStructure}
            linkedOhCaseId={linkedOhCaseId}
            documentBaseApi={`/api/physiotherapy/customer/${customerId}`}
        />
    );
};

export { PhysiotherapyMgrReferralForm };
